import React, { useState, useEffect } from "react";
import { SmallPlainInsetBtn, ToggleSlider } from "../buttons";
import { PencilIcon, TrashCanIcon, EyeIcon, EyeSlashIcon } from "../icons";

const CampaignMetricsCard = ({
  campaign,
  onEdit,
  onDelete,
  onAutoPayToggle,
}) => {
  const [autoPayEnabled, setAutoPayEnabled] = useState(
    campaign.autoPaymentsEnabled
  );
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setAutoPayEnabled(campaign.autoPaymentsEnabled);
  }, [campaign.autoPaymentsEnabled]);

  useEffect(() => {
    console.log("CampaignMetricsCard - onAutoPayToggle:", onAutoPayToggle);
  }, [onAutoPayToggle]);

  const handleAutoPayToggle = async () => {
    // We assume the parent handles error messaging (e.g., for expired campaigns)
    const newStatus = !autoPayEnabled;
    try {
      await onAutoPayToggle(campaign.id, newStatus);
      setAutoPayEnabled(newStatus);
    } catch (err) {
      console.error("Error toggling autopay in backend (child):", err);
      // The parent's error message will be set; the child doesn't need to do more.
    }
  };

  function handleViewMetrics() {
    // We assume setExpanded(true) has been called already.
    setTimeout(() => {
      const el = document.getElementById(`metricBottom${campaign.id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  }
  
  // Dynamic labels
  // Dynamic labels based on paymentType
  const dailyAllowableReachLimitLabel =
    campaign.paymentType === "impressions"
      ? "Daily Impressions Limit"
      : "Daily Clicks Limit";

  const totalBudgetAllowableReachLimitLabel =
    campaign.paymentType === "impressions"
      ? "Total Budget Impressions"
      : "Total Budget Clicks";

  const currentReachLabel =
    campaign.paymentType === "impressions"
      ? "Current Impressions"
      : "Current Clicks";

  const dailyCostLimitLabel =
    campaign.paymentType === "impressions"
      ? "Daily Impressions Cost Limit"
      : "Daily Clicks Cost Limit";

  // Derived values
  const currentReach =
    campaign.paymentType === "impressions"
      ? campaign.currentImpressions
      : campaign.currentClicks;

  return (
    <div className="campaign-metrics-card">
      <div className="campaign-header">
        <h3>{campaign.title}</h3>
        <div className="metric-action-buttons">
          <SmallPlainInsetBtn
            image={
              <PencilIcon
                height="15px"
                width="15px"
                style={{ marginRight: "3px" }}
              />
            }
            text="Edit"
            onClick={() => onEdit(campaign)}
          />
          <SmallPlainInsetBtn
            image={
              <TrashCanIcon
                height="15px"
                width="15px"
                style={{ marginRight: "3px" }}
              />
            }
            text="Delete"
            onClick={() => onDelete(campaign.id, campaign.sourceId)}
          />
          <SmallPlainInsetBtn
            image={
              expanded ? (
                <EyeSlashIcon
                  height="15px"
                  width="15px"
                  style={{ marginRight: "3px" }}
                  fillColor="var(--color-bloodred)"
                />
              ) : (
                <EyeIcon
                  height="15px"
                  width="15px"
                  style={{ marginRight: "3px" }}
                  fillColor="var(--color-bloodred)"
                />
              )
            }
            text={expanded ? "Hide Metrics" : "View Metrics"}
            onClick={(e) => {
              e.preventDefault();
              if (!expanded) {
                setExpanded(true);
                handleViewMetrics();
              } else {
                setExpanded(false);
              }
            }}
            
          />
          <ToggleSlider
            checked={Boolean(campaign.autoPaymentsEnabled)}
            handleChangeSlider={handleAutoPayToggle}
            content={campaign.autoPaymentsEnabled ? "ON" : "OFF"}
            sliderWrapperStyle={{
              display: "inline-block",
              marginLeft: "10px",
            }}
          />
        </div>
      </div>
      {expanded && (
        <div className="campaign-details">
          <p>
            <strong>Campaign Name:</strong> {campaign.campaignName}
          </p>
          <p>
            <strong>Advertising Type:</strong> {campaign.commercialType}
          </p>
          <p>
            <strong>Ad Category:</strong>{" "}
            {campaign.relationalSelectedCategoryName}
          </p>
          <p>
            <strong>Category Ad Promo:</strong> {campaign.relationalAdPromoName}
          </p>
          <p>
            <strong>Status:</strong> {campaign.campaignStatus}
          </p>
          <p>
            <strong>Total Budget:</strong> ${campaign.totalBudget}
          </p>
          <p>
            <strong>Period:</strong>{" "}
            {campaign.paymentSubscription === "1"
              ? "Daily"
              : "Lifetime (One-Time)"}
          </p>

          {/* Conditional rendering based on subscription type */}
          {campaign.paymentSubscription === "1" && (
            <>
              <p>
                <strong>{dailyCostLimitLabel}:</strong> $
                {campaign.dailyCostLimit}
              </p>
              <p>
                <strong>{dailyAllowableReachLimitLabel}:</strong>{" "}
                {campaign.paymentType === "impressions"
                  ? campaign.dailyImpressionsLimit
                  : campaign.dailyClicksLimit}
              </p>
            </>
          )}

          <p>
            <strong>{totalBudgetAllowableReachLimitLabel}:</strong>{" "}
            {campaign.paymentType === "impressions"
              ? campaign.totalBudgetImpressions
              : campaign.totalBudgetClicks}
          </p>
          <p>
            <strong>{currentReachLabel}:</strong> {currentReach}
          </p>
          <p>
            <strong>Cost Per Result:</strong> ${campaign.currentAmountSpent}
          </p>

          <p id={`metricBottom${campaign.id}`}>
            <strong>Started At:</strong>{" "}
            {new Date(campaign.startedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CampaignMetricsCard;
